// components/chat/MeetingModal.jsx
import React, { useState, useEffect, useRef, useMemo } from "react";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const NEON     = "#00FF99";

const CALL_TYPES = [
  { value: "kweryai",  label: "KweryAI Call" },
  { value: "zoom",     label: "Zoom" },
  { value: "meet",     label: "Google Meet" },
  { value: "teams",    label: "Teams" },
  { value: "skype",    label: "Skype" },
  { value: "other",    label: "Other" },
  { value: "inperson", label: "In Person" },
];

const EXTERNAL_PLACEHOLDERS = {
  zoom:  "https://zoom.us/j/...",
  meet:  "https://meet.google.com/...",
  teams: "https://teams.microsoft.com/...",
  skype: "https://join.skype.com/...",
  other: "Paste meeting link...",
};

// Format a Date as a "datetime-local" input value: YYYY-MM-DDTHH:mm (local).
function toLocalInputValue(d) {
  if (!(d instanceof Date) || isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

// Round a Date up to the nearest 30-minute boundary.
function roundToNext30(d) {
  const out = new Date(d);
  const min = out.getMinutes();
  out.setSeconds(0, 0);
  out.setMinutes(min <= 30 ? 30 : 60);
  return out;
}

function defaultStart() {
  return roundToNext30(new Date(Date.now() + 60 * 60 * 1000));
}

function plusOneHour(d) {
  return new Date(d.getTime() + 60 * 60 * 1000);
}

export default function MeetingModal({ open, onClose, onSuccess, initialSlot }) {
  const [title,        setTitle]        = useState("");
  const [description,  setDescription]  = useState("");
  const [startTime,    setStartTime]    = useState("");
  const [endTime,      setEndTime]      = useState("");
  const [callType,     setCallType]     = useState("kweryai");
  const [externalLink, setExternalLink] = useState("");
  const [location,     setLocation]     = useState("");
  const [attendeeIds,  setAttendeeIds]  = useState([]);
  const [users,        setUsers]        = useState([]);
  const [busy,         setBusy]         = useState(false);
  const [error,        setError]        = useState("");

  // Reset + prefill when the modal opens
  useEffect(() => {
    if (!open) return;
    setTitle("");
    setDescription("");
    setCallType("kweryai");
    setExternalLink("");
    setLocation("");
    setAttendeeIds([]);
    setError("");

    let s, e;
    if (initialSlot && initialSlot.start && initialSlot.end) {
      s = new Date(initialSlot.start);
      e = new Date(initialSlot.end);
    } else {
      s = defaultStart();
      e = plusOneHour(s);
    }
    setStartTime(toLocalInputValue(s));
    setEndTime(toLocalInputValue(e));
  }, [open, initialSlot]);

  // Load attendee directory on open
  useEffect(() => {
    if (!open) return;
    fetch(`${API_BASE}/api/users/directory`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then((r) => (r.ok ? r.json() : { data: [] }))
      .then((d) => {
        const list = Array.isArray(d) ? d : (d?.data || []);
        setUsers(list);
      })
      .catch(() => setUsers([]));
  }, [open]);

  // closedRef guard — same pattern as ChatTaskCreateModal
  const closedRef = useRef(false);
  useEffect(() => {
    closedRef.current = false;
    return () => {
      if (!closedRef.current) onClose?.();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sortedUsers = useMemo(
    () => [...users].sort((a, b) => (a.full_name || "").localeCompare(b.full_name || "")),
    [users]
  );

  const toggleAttendee = (id) => {
    setAttendeeIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSubmit = async () => {
    if (!title.trim()) { setError("Title is required."); return; }
    if (!startTime || !endTime) { setError("Start and end time are required."); return; }
    const sDate = new Date(startTime);
    const eDate = new Date(endTime);
    if (isNaN(sDate.getTime()) || isNaN(eDate.getTime())) {
      setError("Invalid date/time."); return;
    }
    if (eDate <= sDate) {
      setError("End time must be after start time."); return;
    }

    setBusy(true);
    setError("");
    try {
      let meeting_link = null;
      if (callType === "kweryai") {
        meeting_link = "kweryai://";
      } else if (callType === "inperson") {
        meeting_link = null;
      } else {
        meeting_link = externalLink.trim() || null;
      }

      const body = {
        title:         title.trim(),
        description:   description.trim() || null,
        start_time:    sDate.toISOString(),
        end_time:      eDate.toISOString(),
        meeting_link,
        location:      location.trim() || null,
        attendee_ids:  attendeeIds,
      };
      const res = await fetch(`${API_BASE}/api/meetings/`, {
        method:  "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization:  `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || `Failed to create meeting (HTTP ${res.status})`);
      }
      closedRef.current = true;
      onSuccess?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-[9999]"
      onClick={() => { closedRef.current = true; onClose?.(); }}
    >
      <div
        className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl p-6 bg-black border"
        style={{ borderColor: "rgba(0,255,153,0.35)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-xl font-semibold" style={{ color: NEON }}>
            Schedule Meeting
          </h2>
          <button
            onClick={() => { closedRef.current = true; onClose?.(); }}
            className="h-9 w-9 rounded-full border grid place-items-center"
            style={{ color: NEON, backgroundColor: "rgba(0,255,153,0.12)", borderColor: "rgba(0,255,153,0.35)" }}
          >✖</button>
        </div>

        <label className="block text-xs text-white/50 mb-1">Title</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Meeting title"
          className="w-full rounded-md p-2 text-sm bg-black/40 border text-white outline-none mb-3"
          style={{ borderColor: "rgba(255,255,255,0.25)" }}
        />

        <label className="block text-xs text-white/50 mb-1">Description (optional)</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          placeholder="Agenda, prep notes, etc."
          className="w-full rounded-md p-2 text-sm bg-black/40 border text-white outline-none mb-3"
          style={{ borderColor: "rgba(255,255,255,0.25)" }}
        />

        <div className="grid grid-cols-2 gap-2 mb-3">
          <div>
            <label className="block text-xs text-white/50 mb-1">Start</label>
            <input
              type="datetime-local"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="w-full rounded-md p-2 text-sm bg-black/40 border text-white outline-none"
              style={{ borderColor: "rgba(255,255,255,0.25)" }}
            />
          </div>
          <div>
            <label className="block text-xs text-white/50 mb-1">End</label>
            <input
              type="datetime-local"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="w-full rounded-md p-2 text-sm bg-black/40 border text-white outline-none"
              style={{ borderColor: "rgba(255,255,255,0.25)" }}
            />
          </div>
        </div>

        <label className="block text-xs text-white/50 mb-1">Meeting type</label>
        <div className="flex flex-wrap gap-1.5 mb-3">
          {CALL_TYPES.map((opt) => {
            const active = callType === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setCallType(opt.value)}
                className="px-3 py-1.5 rounded-full text-xs border transition-colors"
                style={{
                  borderColor: active ? "rgba(0,255,153,0.4)"  : "rgba(255,255,255,0.15)",
                  background:  active ? "rgba(0,255,153,0.12)" : "transparent",
                  color:       active ? NEON                   : "rgba(255,255,255,0.5)",
                }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {callType === "kweryai" && (
          <div
            className="text-xs mb-3 px-3 py-2 rounded-md"
            style={{
              borderLeft: `2px solid ${NEON}`,
              background: "rgba(0,255,153,0.06)",
              color:      "rgba(255,255,255,0.7)",
            }}
          >
            A KweryAI call room will be created automatically when the meeting starts.
          </div>
        )}

        {callType !== "kweryai" && callType !== "inperson" && (
          <>
            <label className="block text-xs text-white/50 mb-1">
              {CALL_TYPES.find((c) => c.value === callType)?.label} link
            </label>
            <input
              type="text"
              value={externalLink}
              onChange={(e) => setExternalLink(e.target.value)}
              placeholder={EXTERNAL_PLACEHOLDERS[callType] || "Paste meeting link..."}
              className="w-full rounded-md p-2 text-sm bg-black/40 border text-white outline-none mb-3"
              style={{ borderColor: "rgba(255,255,255,0.25)" }}
            />
          </>
        )}

        <label className="block text-xs text-white/50 mb-1">Location (optional)</label>
        <input
          type="text"
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="Room A / Remote / Hybrid"
          className="w-full rounded-md p-2 text-sm bg-black/40 border text-white outline-none mb-3"
          style={{ borderColor: "rgba(255,255,255,0.25)" }}
        />

        <label className="block text-xs text-white/50 mb-1">
          Attendees {attendeeIds.length > 0 && (
            <span style={{ color: NEON }}>({attendeeIds.length} selected)</span>
          )}
        </label>
        <div
          className="rounded-md border bg-black/40 mb-4 max-h-48 overflow-y-auto"
          style={{ borderColor: "rgba(255,255,255,0.25)" }}
        >
          {sortedUsers.length === 0 ? (
            <div className="text-xs text-white/40 p-3 text-center">No teammates available</div>
          ) : (
            sortedUsers.map((u) => {
              const checked = attendeeIds.includes(u.id);
              return (
                <label
                  key={u.id}
                  className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-white/5 border-b"
                  style={{ borderColor: "rgba(255,255,255,0.05)" }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleAttendee(u.id)}
                    className="accent-[#00FF99]"
                  />
                  <span className="text-sm text-white/85 flex-1 truncate">
                    {u.full_name || "Unnamed"}
                  </span>
                  <span className="text-xs text-white/40 truncate">{u.email}</span>
                </label>
              );
            })
          )}
        </div>

        {error && <p className="text-red-400 text-sm mb-3">{error}</p>}

        <div className="flex justify-end gap-2">
          <button
            onClick={() => { closedRef.current = true; onClose?.(); }}
            className="h-10 px-4 rounded-md border text-sm"
            style={{ color: "#fff", borderColor: "rgba(255,255,255,0.25)" }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={busy}
            className="h-10 px-4 rounded-md border text-sm"
            style={{
              color: NEON,
              backgroundColor: "rgba(0,255,153,0.15)",
              borderColor: "rgba(0,255,153,0.35)",
              opacity: busy ? 0.6 : 1,
            }}
          >
            {busy ? "Scheduling…" : "Schedule"}
          </button>
        </div>
      </div>
    </div>
  );
}
